import React from 'react';
import PropTypes from 'prop-types';
import usePermission from '../../Hooks/usePermission';

/**
 * Component that conditionally renders children based on user permissions.
 *
 * Usage examples:
 *
 * // Single permission check
 * <PermissionGuard permission="asset:create">
 *   <CreateAssetButton />
 * </PermissionGuard>
 *
 * // Multiple permissions (ANY - default)
 * <PermissionGuard permissions={['asset:read', 'service:read']}>
 *   <DashboardWidget />
 * </PermissionGuard>
 *
 * // Multiple permissions (ALL required)
 * <PermissionGuard permissions={['asset:create', 'asset:update']} operator="all">
 *   <AssetEditorWidget />
 * </PermissionGuard>
 *
 * // With fallback content
 * <PermissionGuard permission="role:manage" fallback={<NoAccessMessage />}>
 *   <RoleManagementWidget />
 * </PermissionGuard>
 *
 * // Role-based check
 * <PermissionGuard role="ROLE_ADMINISTRATOR">
 *   <AdminOnlyContent />
 * </PermissionGuard>
 *
 * // Super admin only
 * <PermissionGuard superAdminOnly>
 *   <SuperAdminContent />
 * </PermissionGuard>
 *
 * // Admin only (includes super admin)
 * <PermissionGuard adminOnly>
 *   <AdminContent />
 * </PermissionGuard>
 */
const PermissionGuard = ({
  permission,
  permissions,
  operator = 'any',
  role,
  adminOnly = false,
  superAdminOnly = false,
  superAdminBypass = true,
  children,
  fallback = null,
}) => {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isSuperAdmin,
    isAdmin,
  } = usePermission();

  const checkAccess = () => {
    // Super admin bypass (unless checking for super admin specifically)
    if (superAdminBypass && !superAdminOnly && isSuperAdmin) {
      return true;
    }

    // Super admin only check
    if (superAdminOnly) {
      return isSuperAdmin;
    }

    // Admin only check (includes super admin)
    if (adminOnly) {
      return isAdmin;
    }

    // Role-based check
    if (role) {
      if (role === 'ROLE_ADMINISTRATOR') {
        return isAdmin;
      }
      return hasRole(role);
    }

    // Single permission check
    if (permission) {
      return hasPermission(permission);
    }

    // Multiple permissions check
    if (permissions && permissions.length > 0) {
      if (operator === 'all') {
        return hasAllPermissions(permissions);
      }
      return hasAnyPermission(permissions);
    }

    // No permission criteria specified - deny by default
    return false;
  };

  if (checkAccess()) {
    return children;
  }

  return fallback;
};

PermissionGuard.propTypes = {
  /** Single permission to check (e.g., 'asset:read') */
  permission: PropTypes.string,
  /** Multiple permissions to check */
  permissions: PropTypes.arrayOf(PropTypes.string),
  /** Logical operator for multiple permissions: 'any' or 'all' */
  operator: PropTypes.oneOf(['any', 'all']),
  /** Role name to check (e.g., 'ROLE_ADMINISTRATOR') */
  role: PropTypes.string,
  /** If true, only admins can see the content */
  adminOnly: PropTypes.bool,
  /** If true, only super admins can see the content */
  superAdminOnly: PropTypes.bool,
  /** If true, super admins bypass permission checks (default: true) */
  superAdminBypass: PropTypes.bool,
  /** Content to render if permission check passes */
  children: PropTypes.node.isRequired,
  /** Content to render if permission check fails (default: null) */
  fallback: PropTypes.node,
};

/**
 * Higher-order component for permission-based access control.
 * Wraps a component and only renders it if user has required permissions.
 *
 * Usage:
 * const ProtectedComponent = withPermission(MyComponent, { permission: 'asset:read' });
 * const AdminComponent = withPermission(MyComponent, { adminOnly: true });
 */
export const withPermission = (WrappedComponent, permissionConfig) => {
  const WithPermissionComponent = (props) => (
    <PermissionGuard {...permissionConfig}>
      <WrappedComponent {...props} />
    </PermissionGuard>
  );

  WithPermissionComponent.displayName = `withPermission(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithPermissionComponent;
};

/**
 * Component for permission-based conditional rendering with render props.
 * Provides more control than PermissionGuard.
 *
 * Usage:
 * <PermissionCheck permission="asset:create">
 *   {(hasAccess) => hasAccess ? <CreateButton /> : <DisabledButton />}
 * </PermissionCheck>
 */
export const PermissionCheck = ({
  permission,
  permissions,
  operator = 'any',
  children,
}) => {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
  } = usePermission();

  const checkAccess = () => {
    // Super admin always has access
    if (isSuperAdmin) {
      return true;
    }

    // Single permission check
    if (permission) {
      return hasPermission(permission);
    }

    // Multiple permissions check
    if (permissions && permissions.length > 0) {
      if (operator === 'all') {
        return hasAllPermissions(permissions);
      }
      return hasAnyPermission(permissions);
    }

    return false;
  };

  if (typeof children === 'function') {
    return children(checkAccess());
  }

  return checkAccess() ? children : null;
};

PermissionCheck.propTypes = {
  /** Single permission to check */
  permission: PropTypes.string,
  /** Multiple permissions to check */
  permissions: PropTypes.arrayOf(PropTypes.string),
  /** Logical operator for multiple permissions */
  operator: PropTypes.oneOf(['any', 'all']),
  /** Render prop or regular children */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
};

export default PermissionGuard;
